import {
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { Button, Indicator, Tooltip } from "@mantine/core";
import { IconLayoutColumns } from "@tabler/icons-react";
import TableColumnsContent from "./TableColumnsContent";
import { updateProfile } from "../../../../api/UserAPI";
import { notifications } from "@mantine/notifications";
import { useProfileStore } from "../../../../stores/UserStore";
import PopOver from "../../../../components/View/PopOver";
import { useParams } from "react-router-dom";

const TableColumns = ({
  pageOrderName,
  whiteButtons,
  tableId,
  getColumnActions,
}) => {
  const lang = useLanguage((s) => s.language);
  const { contactListId } = useParams()
  const [edited, setEdited] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiRef = useGridApiContext();
  const columnVisibilityModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector
  );

  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);

  const hiddenColumns = useMemo(() => {
    let x = [];
    Object.keys(columnVisibilityModel).forEach((key) =>
      columnVisibilityModel[key] === false ? x.push(key) : null
    );

    return x;
  }, [columnVisibilityModel]);

  const handleSave = () => {
    if (!edited || !pageOrderName) {
      return;
    }
    const allColumns = [...columns];

    let newOrder = allColumns.map((column) => column.field);

    let visibleColumns = [];
    allColumns.forEach((column) => {
      if (Object.keys(columnVisibilityModel).includes(column.field)) {
        if (columnVisibilityModel?.[column.field])
          visibleColumns.push(column.field);
      } else visibleColumns.push(column.field);
    });

    let columnsSize = {};
    columns.forEach((column) => {
      columnsSize[column.field] = column.width;
    });

    const newVisibleColumns = visibleColumns;

    const profile = useProfileStore.getState().profile;
    const setProfile = useProfileStore.getState().setProfile;

    let hidden_columns = columns
      .filter((x) => !newVisibleColumns.includes(x.field))
      .map((x) => x.field);

    let newData = {};

    const savedListOrder = profile.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id?.[contactListId];

    if (savedListOrder) {
      newData = {
        saved_order: {
          ...(profile?.saved_order || {}),
          "contact_list_fetch_contacts_order_by_contact_list_id": {
            ...(profile?.saved_order?.contact_list_fetch_contacts_order_by_contact_list_id || {}),
            [contactListId]: {
              hidden_columns: hidden_columns,
              order_columns: newOrder.filter((x) => x !== "actions"),
              size_columns: columnsSize,
            },
          },
        },
      }
    }
    else {
      newData = {
        saved_order: {
          ...(profile?.saved_order || {}),
          [pageOrderName]: {
            hidden_columns: hidden_columns,
            order_columns: newOrder.filter((x) => x !== "actions"),
            size_columns: columnsSize,
          },
        },
      }
    }

    setLoading(true);
    updateProfile(null, newData)
      .then((response) => {
        notifications.show({
          message: lang.table.toolbar.columns.visibility_saved,
          color: "tertiary.8",
        });
        setProfile({ ...profile, ...newData });
      })
      .finally(() => setLoading(false));
  };

  const onChange = (column, newValue) => {
    apiRef.current.setColumnVisibility(column, newValue);

    setEdited(true);
  };

  const hideAll = (closePopover) => {
    const newModel = {};

    columns.forEach((col) => {
      newModel[col.field] = false;
    });

    apiRef.current.setColumnVisibilityModel(newModel);

    closePopover();
  };

  const showAll = (closePopover) => {
    const newModel = {};

    columns.forEach((col) => {
      newModel[col.field] = true;
    });
    apiRef.current.setColumnVisibilityModel(newModel);

    closePopover();
  };

  return (
    <PopOver
      opener={(handleOpen, ref) =>
        whiteButtons ? (
          <Tooltip label={lang.table.toolbar.columns.button_tooltip}>
            <Button
              radius={"md"}
              variant={"subtle"}
              className="test table-columns-button"
              style={{
                borderColor: "var(--mantine-color-primary-0)",
                background: "#fff",
              }}
              id={"table-columns-" + tableId}
              ml={16}
              onClick={handleOpen}
              ref={ref}
              loading={loading}
            >
              <Indicator
                label={hiddenColumns.length}
                color="red"
                offset={2}
                disabled={hiddenColumns.length === 0}
                styles={{
                  indicator: {
                    fontSize: 9,
                    padding: "6px",
                  },
                }}
              >
                <IconLayoutColumns size={20} />
              </Indicator>
              <span style={{ marginLeft: 6 }}>
                {lang.table.toolbar.columns.button_label}
              </span>
            </Button>
          </Tooltip>
        ) : (
          <Tooltip label={lang.table.toolbar.columns.button_tooltip}>
            <Indicator
              label={hiddenColumns.length}
              color="red"
              offset={2}
              disabled={hiddenColumns.length === 0}
              // withBorder
              size={16}
            >
              <Button
                loading={loading}
                ml={16}
                fz={14}
                fw={500}
                variant={"subtle"}
                size={"sm"}
                miw={0}
                w={38}
                p={0}
                radius={"md"}
                onClick={handleOpen}
                ref={ref}
              >
                <IconLayoutColumns size={24} />
              </Button>
            </Indicator>
          </Tooltip>
        )
      }
      popoverChildren={(handleClose) => (
        <TableColumnsContent
          columnVisibility={columnVisibilityModel}
          columns={columns.map(x => ({ id: x.field, ...x }))}
          onColumnCheck={onChange}
          hideAll={hideAll}
          showAll={showAll}
          pageOrderName={pageOrderName}
          getColumnActions={getColumnActions}
          handleClose={handleClose}
          isListContent={tableId === "list-content-table"}
          setEdited={setEdited}
        />
      )}
      onClose={handleSave}
    />
  );
};

export default TableColumns;
